<template>
  <div :class="spinnerClasses">
    <div class="ui-spinner__circle"></div>
    <span v-if="text" class="ui-spinner__text">{{ text }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { Size, Variant } from "./types";

interface Props {
  size?: Size;
  variant?: Variant;
  text?: string;
  inline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  variant: "primary",
  inline: false,
});

const spinnerClasses = computed(() => {
  const classes = ["ui-spinner"];
  classes.push(`ui-spinner--${props.size}`);
  classes.push(`ui-spinner--${props.variant}`);
  if (props.inline) classes.push("ui-spinner--inline");
  return classes;
});
</script>

<style scoped>
.ui-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.ui-spinner--inline {
  flex-direction: row;
  gap: 8px;
}

.ui-spinner__circle {
  border-radius: 50%;
  border-style: solid;
  border-color: currentColor;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Sizes */
.ui-spinner--xs .ui-spinner__circle {
  width: 12px;
  height: 12px;
  border-width: 2px;
}

.ui-spinner--sm .ui-spinner__circle {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

.ui-spinner--md .ui-spinner__circle {
  width: 24px;
  height: 24px;
  border-width: 3px;
}

.ui-spinner--lg .ui-spinner__circle {
  width: 32px;
  height: 32px;
  border-width: 3px;
}

.ui-spinner--xl .ui-spinner__circle {
  width: 48px;
  height: 48px;
  border-width: 4px;
}

/* Variants */
.ui-spinner--primary {
  color: #3e3efe;
}

.ui-spinner--secondary {
  color: #6b7280;
}

.ui-spinner--success {
  color: #10b981;
}

.ui-spinner--warning {
  color: #f59e0b;
}

.ui-spinner--danger {
  color: #ef4444;
}

.ui-spinner__text {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}
</style>
